import React, { useState } from 'react';
import { 
  Building, TrendingUp, HeartPulse, Sprout, MapPin, FileSearch,
  ShieldPlus, Pill, Cigarette, Apple, Droplet, Zap,
  SearchCheck, Brain, Clock, Lightbulb, ChevronRight, Mail, Phone, MapPinned, ArrowRight,
  Menu, X, FileText, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import axios from 'axios';
import ZedworxLogo from '../components/ZedworxLogo';
import { services, clientIndustries, whyChooseUs, stats } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  building: Building,
  'trending-up': TrendingUp,
  'heart-pulse': HeartPulse,
  sprout: Sprout,
  'map-pin': MapPin,
  'file-search': FileSearch,
  'shield-plus': ShieldPlus,
  pill: Pill,
  cigarette: Cigarette,
  apple: Apple,
  droplet: Droplet,
  zap: Zap,
  'search-check': SearchCheck,
  brain: Brain,
  clock: Clock,
  lightbulb: Lightbulb
};

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({ name: '', email: '', company: '', message: '' });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
  };

  const handleNavClick = (sectionId) => {
    setIsMobileMenuOpen(false);
    document.getElementById(sectionId).scrollIntoView({ behavior: 'smooth' });
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="dark-theme-page">
      {/* Header */}
      <header className="dark-header">
        <div className="header-logo">
          <ZedworxLogo width={180} height={50} variant="header" />
        </div>
        
        {/* Desktop Navigation */}
        <nav className="dark-nav">
          <a href="#services" className="dark-nav-link">Services</a>
          <a href="#industries" className="dark-nav-link">Industries</a>
          <a href="#about" className="dark-nav-link">About</a>
          <a href="#contact" className="dark-nav-link">Contact</a>
        </nav>

        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-button"
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'mobile-menu-open' : ''}`}>
        <nav className="mobile-nav">
          <button onClick={() => handleNavClick('services')} className="mobile-nav-link">
            Services
          </button>
          <button onClick={() => handleNavClick('industries')} className="mobile-nav-link">
            Industries
          </button>
          <button onClick={() => handleNavClick('about')} className="mobile-nav-link">
            About
          </button>
          <button onClick={() => handleNavClick('contact')} className="mobile-nav-link">
            Contact
          </button>
        </nav>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            Navigate Complex Regulatory Landscapes with Confidence
          </h1>
          <p className="hero-description">
            Navigate South Africa's complex regulatory landscape with confidence. <span className="brand-zed">ZED</span><span className="brand-worx">WORX</span> provides meticulous tracking of legislative developments across key sectors, empowering businesses with actionable intelligence for strategic decision-making.
          </p>
          <p className="hero-description hero-description-second">
            Legislative Intelligence Services keeps clients ahead of developments through concise briefing notes, high-level presentations, and timely updates on government affairs.
          </p>
          <div className="hero-buttons">
            <Button className="btn-primary" onClick={scrollToContact}>
              Get Started
              <ArrowRight size={20} />
            </Button>
            <Button className="btn-secondary" onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}>
              Our Services
            </Button>
          </div>
          
          {/* Stats */}
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Zedworx & SALIT Section - MOVED TO FRONT */}
      <section id="about" className="section-container">
        <div className="section-header">
          <h2 className="section-title">Why Choose <span className="brand-zed">ZED</span><span className="brand-worx">WORX</span>?</h2>
          <p className="section-subtitle">
            Smart work, extensive experience, meticulous tracking, and timely intelligence - delivering actionable insights that drive strategic business decisions.
          </p>
        </div>
        
        <div className="why-grid">
          {whyChooseUs.map((item) => {
            const Icon = iconMap[item.icon];
            
            return (
              <div key={item.id} className="why-card-expandable">
                <div className="why-card-header">
                  <div className="why-icon">
                    <Icon size={40} />
                  </div>
                  <h3 className="why-title">{item.title}</h3>
                  <p className="why-description">{item.description}</p>
                </div>
                
                <Accordion type="single" collapsible className="why-accordion">
                  <AccordionItem value="details">
                    <AccordionTrigger className="accordion-trigger-custom">
                      Learn More & View Examples
                    </AccordionTrigger>
                    <AccordionContent className="accordion-content-custom">
                      {/* Reports */}
                      <div className="expanded-section">
                        <h4 className="expanded-title">
                          <FileText size={18} />
                          Resources & Reports
                        </h4>
                        <ul className="report-list">
                          {item.expandedContent.reports.map((report, idx) => (
                            <li key={idx} className="report-item">
                              <Download size={16} />
                              <a href={report.link} className="report-link">
                                {report.title}
                              </a>
                              <span className="report-type">{report.type}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Analysis */}
                      <div className="expanded-section">
                        <h4 className="expanded-title">Detailed Analysis</h4>
                        <p className="analysis-text">{item.expandedContent.analysis}</p>
                      </div>
                      
                      {/* Case Study */}
                      <div className="expanded-section case-study">
                        <h4 className="expanded-title">Case Study</h4>
                        <h5 className="case-study-title">{item.expandedContent.caseStudy.title}</h5>
                        <p className="case-study-preview">{item.expandedContent.caseStudy.preview}</p>
                      </div>
                      
                      {/* Key Insights */}
                      <div className="expanded-section">
                        <h4 className="expanded-title">Key Insights</h4>
                        <ul className="insights-list">
                          {item.expandedContent.insights.map((insight, idx) => (
                            <li key={idx} className="insight-item">
                              <ChevronRight size={16} />
                              {insight}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            );
          })}
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-container">
        <div className="section-header">
          <h2 className="section-title">Legislative Monitoring Services</h2>
          <p className="section-subtitle">
            Specialised legislative intelligence across critical areas: Fiscal Oversight, Health Care & Health Services, and Manufacturing: Tobacco, Food & Beverages.
          </p>
        </div>
        
        <div className="services-grid">
          {services.map((service) => {
            const Icon = iconMap[service.icon];
            return (
              <div key={service.id} className="service-card-expandable">
                <div className="service-card-header">
                  <div className="service-icon">
                    <Icon size={32} />
                  </div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                </div>
                
                <Accordion type="single" collapsible className="service-accordion">
                  <AccordionItem value="details">
                    <AccordionTrigger className="accordion-trigger-custom">
                      View Sample Reports & Analysis
                    </AccordionTrigger>
                    <AccordionContent className="accordion-content-custom">
                      {/* Reports */}
                      <div className="expanded-section">
                        <h4 className="expanded-title">
                          <FileText size={18} />
                          Sample Reports
                        </h4>
                        <ul className="report-list">
                          {service.expandedContent.reports.map((report, idx) => (
                            <li key={idx} className="report-item">
                              <Download size={16} />
                              <a href={report.link} className="report-link">
                                {report.title}
                              </a>
                              <span className="report-type">{report.type}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Analysis */}
                      <div className="expanded-section">
                        <h4 className="expanded-title">Analysis Overview</h4>
                        <p className="analysis-text">{service.expandedContent.analysis}</p>
                      </div>
                      
                      {/* Case Study - only show if exists */}
                      {service.expandedContent.caseStudy && (
                        <div className="expanded-section case-study">
                          <h4 className="expanded-title">Case Study</h4>
                          <h5 className="case-study-title">{service.expandedContent.caseStudy.title}</h5>
                          <p className="case-study-preview">{service.expandedContent.caseStudy.preview}</p>
                        </div>
                      )}
                      
                      {/* Key Insights */}
                      <div className="expanded-section">
                        <h4 className="expanded-title">Key Insights</h4>
                        <ul className="insights-list">
                          {service.expandedContent.insights.map((insight, idx) => (
                            <li key={idx} className="insight-item">
                              <ChevronRight size={16} />
                              {insight}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            );
          })}
        </div>
      </section>

      {/* Client Industries Section */}
      <section id="industries" className="section-container section-alt">
        <div className="section-header">
          <h2 className="section-title">Industries We Serve</h2>
          <p className="section-subtitle">
            Specialised legislative and budget intelligence for businesses operating in sectors affected by government service delivery and public funding allocation.
          </p>
        </div>
        
        <div className="industries-grid">
          {clientIndustries.map((industry) => {
            const Icon = iconMap[industry.icon];
            return (
              <div key={industry.id} className="industry-card">
                <div className="industry-icon">
                  <Icon size={28} />
                </div>
                <h3 className="industry-name">{industry.name}</h3>
                <p className="industry-description">{industry.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-container section-contact">
        <div className="contact-container">
          <div className="contact-info">
            <h2 className="contact-title">Contact Us</h2>
            <p className="contact-description">
              Connect with our team to secure your strategic advantage in the South African legislative environment.
            </p>
            
            <div className="contact-details">
              <div className="contact-detail-item">
                <Mail size={24} />
                <div>
                  <div className="contact-detail-label">Email - General Inquiries</div>
                  <div className="contact-detail-value">info@zedworx.co.za</div>
                </div>
              </div>
              
              <div className="contact-detail-item">
                <Mail size={24} />
                <div>
                  <div className="contact-detail-label">Email</div>
                  <div className="contact-detail-value">reza@zedworx.co.za</div>
                </div>
              </div>
              
              <div className="contact-detail-item">
                <Mail size={24} />
                <div>
                  <div className="contact-detail-label">Email</div>
                  <div className="contact-detail-value">shuaib@zedworx.co.za</div>
                </div>
              </div>
              
              <div className="contact-detail-item">
                <Phone size={24} />
                <div>
                  <div className="contact-detail-label">WhatsApp</div>
                  <div className="contact-detail-value">+27 82 301 1928</div>
                </div>
              </div>
              
              <div className="contact-detail-item">
                <Phone size={24} />
                <div>
                  <div className="contact-detail-label">WhatsApp</div>
                  <div className="contact-detail-value">+27 82 411 3542</div>
                </div>
              </div>
              
              <div className="contact-detail-item">
                <MapPinned size={24} />
                <div>
                  <div className="contact-detail-label">Website</div>
                  <div className="contact-detail-value">www.zedworx.co.za</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="contact-form-wrapper">
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name" className="form-label">Name</label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                  placeholder="Your name"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email" className="form-label">Email</label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                  placeholder="your.email@company.com"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="company" className="form-label">Company</label>
                <Input
                  id="company"
                  name="company"
                  type="text"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                  placeholder="Your company name"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="message" className="form-label">Message</label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Tell us about your legislative monitoring needs..."
                  rows={5}
                />
              </div>
              
              <Button 
                type="submit" 
                className="btn-primary btn-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                {!isSubmitting && <ChevronRight size={20} />}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <div className="footer-logo-container">
              <ZedworxLogo width={160} height={45} variant="footer" />
            </div>
            <p className="footer-tagline">
              Trusted Legislative Intelligence Partner - Navigate South Africa's complex regulatory landscape with confidence.
            </p>
          </div>
          
          <div className="footer-links">
            <div className="footer-column">
              <h4 className="footer-column-title">Services</h4>
              <a href="#services" className="footer-link">Fiscal Oversight</a>
              <a href="#services" className="footer-link">Health Care</a>
              <a href="#services" className="footer-link">Manufacturing</a>
            </div>
            
            <div className="footer-column">
              <h4 className="footer-column-title">Industries</h4>
              <a href="#industries" className="footer-link">Health Care Services</a>
              <a href="#industries" className="footer-link">Tobacco Industry</a>
              <a href="#industries" className="footer-link">Water & Sanitation</a>
              <a href="#industries" className="footer-link">Food & Beverages</a>
              <a href="#industries" className="footer-link">Provincial & Municipal</a>
            </div>
            
            <div className="footer-column">
              <h4 className="footer-column-title">Company</h4>
              <a href="#about" className="footer-link">About Us</a>
              <a href="#contact" className="footer-link">Contact</a>
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>&copy; 2024 Zedworx. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
